import { ANSWER_OPTIONS } from "@/assets/constants/constants";
import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import InputSelect from "@/components/input-select/InputSelect";
import InputTextarea from "@/components/input-textarea/InputTextarea";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import type { IAnswer } from "@/types/types";
import { useRef, useState, type FC } from "react";
import ImageUploader from "./image-uploader/ImageUploader";
import { uploadImages } from "./utils/upload-images";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateAnswer } from "@/lib/services/answers";
import { appToast } from "@/lib/toast";
import { useLoader } from "@/context/loader-context/LoaderContext";
import { useParams } from "react-router-dom";

interface AnswerFormProps {
  item: IAnswer;
  onClose: () => void;
}

interface IAnswerForm {
  status: string;
  comment: string;
  files: File[];
  images: string[];
}

type AnswerFormKeys = keyof IAnswerForm;

const AnswerForm: FC<AnswerFormProps> = ({ item, onClose }) => {
  const refButton = useRef<boolean>(false);
  const { showLoader, hideLoader } = useLoader();
  const [formData, setFormData] = useState<IAnswerForm>({
    status: item?.status ?? "",
    comment: item?.comment ?? "",
    files: [],
    images: item?.images ?? [],
  });
  const queryClient = useQueryClient();
  const queryKey = `answers-${item.phaseId}`;
  const { inspectionId } = useParams() as { inspectionId: string };

  const translate = useTranslate();

  const { mutateAsync: update } = useMutation({
    mutationFn: updateAnswer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      hideLoader();
      onClose();
    },
    onError: (error) => {
      refButton.current = false;
      appToast.error(translate("error"), error.message);
      hideLoader();
    },
  });

  const isValid = formData.status.trim().length > 0;

  const setFiles = (data: File[]) => {
    setFormData((prev) => {
      return { ...prev, files: data };
    });
  };

  const handleOnEdit = (id: AnswerFormKeys, value: any) => {
    setFormData((prev) => {
      const tmp = { ...prev } as any;
      tmp[id] = value;
      return tmp;
    });
  };

  const submitImages = async () => {
    const { ok_files, ko_files } = await uploadImages(
      formData.files,
      `answers/${inspectionId}/question-${item.questionId}/`,
    );
    return { ok_files, ko_files };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isValid || refButton.current) return;
    refButton.current = true;
    showLoader();

    const { ok_files, ko_files } = await submitImages();
    try {
      let allImages = [] as string[];
      if (item.images && item.images.length > 0) {
        allImages = [...item.images];
      }
      await update({
        inspectionId: Number(item.inspectionId),
        phaseId: Number(item.phaseId),
        body: {
          questionId: item.questionId,
          status: formData.status,
          comment: formData.comment,
          images: [...allImages, ...ok_files],
        },
        id: item.id,
      });

      if (ko_files.length > 0) {
        appToast.error(
          translate("error"),
          translate("filesNotUploaded", [ko_files.join(", ")]),
        );
      }
    } catch (error: any) {
      appToast.error(translate("error"), error.message);
    }
  };

  const handleOnClose = () => {
    onClose();
  };

  return (
    <div data-testid="answer-form" className="space-y-4">
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-4">
          <InputSelect
            label={translate("finishTask")}
            required
            placeholder={translate("selectAnswer")}
            onChange={(value) => handleOnEdit("status", value)}
            options={ANSWER_OPTIONS}
            value={formData.status}
            id={"status"}
          />
          <InputTextarea
            label={translate("addDescription")}
            placeholder={translate("addDescription")}
            value={formData.comment}
            onChange={(value) => handleOnEdit("comment", value)}
            size={"large"}
            name="comment"
          />

          <ImageUploader
            images={formData.images}
            files={formData.files}
            onChange={setFiles}
            //maxFiles={3}
            buttonText={translate("addImage")}
            cameraButtonText={translate("takePhoto")}
          />
        </div>
        <div className="border my-6" />
        <div className="flex gap-4 flex-col-reverse md:flex-row md:justify-between">
          <div className="flex">
            <SecondaryButton
              label={translate("cancel")}
              colour={"red"}
              onClick={handleOnClose}
            />
          </div>
          <div>
            <PrimaryButton
              type="submit"
              label={translate("saveChanges")}
              colour={"red"}
              disabled={!isValid}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default AnswerForm;
